import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import * as I from '../../../../../interfaces/merchants/resources/books';
import * as Actions from '../../../../../redux/actions/merchants/resources/books';

import BooksStep, {
  StepItems,
} from '../../../../../components/books/model1/BooksStep';
import BookingWithPageDetail from '../common/BookingWithPageDetail';
import InputBookingDateTime from '../InputBookingDateTime';
import InputBookingTarget from '../InputBookingTarget';
import InputCompletion from '../InputCompletion';
import InputCompletionFailed from '../InputCompletionFailed';
import InputConfirmation from '../InputConfirmation';
import InputPayment from '../InputPayment';
import InputRequireLogin from '../InputRequireLogin';
import InputUserInfo from '../InputUserInfo';

interface StateProps {
  merchantResource: I.MerchantResource;
  publicId: string;
  step: number;
  postable: boolean;
  settings: I.SettingsModelTypeCourse;
  renew?: boolean;
}

interface DispatchProps {
  actions: {
    goTo: (step: number) => () => void;
    onSubmit: (step: number) => () => void;
    onFinalSubmit: (step: number) => () => void;
  };
}

type Props = StateProps & DispatchProps;

const FULL_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];

const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

class ModelTypeCourse extends React.Component<Props> {
  private renderRequireLoginAt(step: number) {
    return (
      <InputRequireLogin
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        renew={this.props.renew}
      />
    );
  }

  private renderInputBookingTargetAt(step: number) {
    return (
      <BookingWithPageDetail {...this.props.merchantResource}>
        <InputBookingTarget
          modelType={this.props.settings.modelType}
          settings={this.props.settings.bookingTargets}
          postable={this.props.postable}
          onSubmit={this.props.actions.onSubmit(step)}
          onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
          renew={this.props.renew}
          withExpressFlow={true}
        />
      </BookingWithPageDetail>
    );
  }

  private renderInputBookingDateTimeAt(step: number, modifierStep: number) {
    return (
      <InputBookingDateTime
        modelType={this.props.settings.modelType}
        settings={this.props.settings.bookingDateTimes}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private renderInputUserInfoAt(step: number, submitLabelIntlId: string) {
    return (
      <InputUserInfo
        modelType={this.props.settings.modelType}
        settings={this.props.settings.userInfo}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        submitLabelIntlId={submitLabelIntlId}
      />
    );
  }

  private renderInputPaymentAt(step: number) {
    return (
      <InputPayment
        modelType={this.props.settings.modelType}
        settings={this.props.settings.payment!}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
      />
    );
  }

  private renderInputConfirmationAt(step: number) {
    return (
      <InputConfirmation
        modelType={this.props.settings.modelType}
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.confirmation}
        postable={this.props.postable}
        onSubmit={this.props.actions.onFinalSubmit(step)}
        onReinputPayment={this.props.actions.goTo(ERROR_REINPUT_PAYMENT_STEP)}
        onReselect={this.props.actions.goTo(ERROR_RESELECT_STEP)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        publicId={this.props.publicId}
      />
    );
  }

  private renderInputCompletion() {
    return (
      <InputCompletion
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        postable={this.props.postable}
        publicId={this.props.publicId}
        withExpressFlow={true}
      />
    );
  }

  private renderFailedRestart() {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload(false)}
      />
    );
  }

  private renderFailedReselect(modifierStep: number) {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.reselect'}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private renderFailedReinput(modifierStep: number) {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.reinput'}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private fullWorkFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => this.renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => this.renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderInputUserInfoAt(3, 'books.submitLabel.payment') },
      4: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => this.renderInputPaymentAt(4)         },
      5: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderInputConfirmationAt(5)    },
      6: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderInputCompletion()         },

      [ERROR_REINPUT_PAYMENT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReinput(4) },
      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReselect(2) },
    }[step];

    return [FULL_WORKFLOW_STEPS, setting];
  }

  private skipPaymentFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => this.renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => this.renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderInputUserInfoAt(3, 'books.submitLabel.confirm') },
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderInputConfirmationAt(4)    },
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderInputCompletion()         },

      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReselect(2) },
    }[step];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }

  public render() {
    const [allSteps, setting] = this.props.merchantResource.paymentStep
      ? this.fullWorkFlowSetting()
      : this.skipPaymentFlowSetting();

    return (
      <>
        {setting.displayProgress && (
          <BooksStep
            allSteps={allSteps}
            current={allSteps.indexOf(setting.currentProgress) + 1}
          />
        )}
        {setting.renderFn()}
      </>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooks,
}: {
  merchantsResourcesBooks: I.State;
}): StateProps => ({
  merchantResource: merchantsResourcesBooks.merchantResource!,
  settings: merchantsResourcesBooks.settings as I.SettingsModelTypeCourse,
  step: merchantsResourcesBooks.step!,
  publicId: merchantsResourcesBooks.merchantId!,
  renew: merchantsResourcesBooks.renew,
  postable: merchantsResourcesBooks.postable!,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    goTo: (step: number) => () => Actions.goTo(step)(dispatch),
    onSubmit: (step: number) => () => Actions.nextStep(step)(dispatch),
    onFinalSubmit: (step: number) => () => Actions.finalStep(step)(dispatch),
  },
});

export default connect(mapStateProps, mapDispatchToProps)(ModelTypeCourse);
